import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Boxes,
  Package,
  ShoppingCart,
  CreditCard,
  Truck,
  PackageCheck,
  Undo2,
  IndianRupee,
  AlertTriangle,
  ArrowUpRight,
  Activity,
  TrendingUp,
} from 'lucide-react';
import { SectionCard } from '@/components/common/SectionCard';
import { ErrorState } from '@/components/common/ErrorState';
import { Sparkline } from '@/components/common/Sparkline';
import { Skeleton } from '@/components/ui/skeleton';
import { dashboardService } from '@/services/dashboard';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/utils';
import { ThroughputChart } from './ThroughputChart';
import { AlertsList } from './AlertsList';
import { ActivityFeed } from './ActivityFeed';
import type {
  DashboardOverview,
  OperationalAlert,
} from '@/types/dashboard';

/**
 * Tenant operational command center. Mirrors the Platform Admin Overview
 * pattern: dated header, hero "needs attention" stat with status chips and
 * a 14-day signup-equivalent sparkline (orders+dispatches), KPI tiles with
 * mini sparklines, attention feed, throughput chart, recent activity.
 */
export function DashboardPage() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['dashboard', 'overview'],
    queryFn: () => dashboardService.overview(),
  });

  if (error) {
    return (
      <div className="space-y-6">
        <Header title="Operational Dashboard" />
        <ErrorState error={error} onRetry={() => refetch()} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Header title="Operational Dashboard" />

      <Hero loading={isLoading} overview={data} />

      <KpiStrip loading={isLoading} overview={data} />

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <SectionCard
          className="lg:col-span-2"
          title={
            <span className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
              Throughput · last 14 days
            </span>
          }
          description="Orders placed, dispatches sent, returns initiated"
          bodyClassName="p-2 sm:p-4"
        >
          {!data || data.throughput.length === 0 ? (
            <Skeleton className="h-48 w-full" />
          ) : (
            <ThroughputChart data={data.throughput} />
          )}
        </SectionCard>

        <SectionCard
          className="lg:col-span-1"
          title={
            <span className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-warning" />
              Attention needed
            </span>
          }
          description="What to look at first today"
          bodyClassName="p-0"
        >
          <AttentionFeed loading={isLoading} alerts={data?.alerts ?? []} kpis={data?.kpis} />
        </SectionCard>
      </div>

      <SectionCard
        title={
          <span className="flex items-center gap-2">
            <Activity className="h-4 w-4 text-muted-foreground" />
            Recent activity
          </span>
        }
        description="Across inventory, orders, dispatch and post-sales"
      >
        <ActivityFeed events={data?.activity ?? []} />
      </SectionCard>
    </div>
  );
}

// ── Header ─────────────────────────────────────────────────────────────

function Header({ title }: { title: string }) {
  const today = new Date();
  const datePretty = today.toLocaleDateString('en-IN', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });
  return (
    <header className="flex flex-col gap-1 pb-1">
      <div className="space-y-1">
        <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          {datePretty}
        </div>
        <h1 className="text-[22px] font-semibold tracking-[-0.015em] text-foreground">
          {title}
        </h1>
      </div>
    </header>
  );
}

// ── Hero ───────────────────────────────────────────────────────────────

function Hero({
  loading,
  overview,
}: {
  loading: boolean;
  overview: DashboardOverview | undefined;
}) {
  if (loading || !overview) {
    return (
      <div className="rounded-xl border border-border/70 bg-card p-6 shadow-elev-1">
        <Skeleton className="h-3 w-24" />
        <Skeleton className="mt-3 h-12 w-32" />
        <Skeleton className="mt-3 h-3 w-64" />
      </div>
    );
  }

  const k = overview.kpis;
  const needsAttention =
    k.paymentPendingOrders +
    k.returnsPending +
    k.refundsPending +
    (k.alertsCount ?? 0);

  // Sparkline: total daily activity (orders + dispatches + returns).
  const trend = overview.throughput.map((p) => p.orders + p.dispatches + p.returns);

  return (
    <div className="flex flex-col gap-6 rounded-xl border border-border/70 bg-card p-6 shadow-elev-1 sm:flex-row sm:items-end sm:justify-between">
      <div className="space-y-2">
        <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          Needs attention today
        </div>
        <div className="flex items-baseline gap-3">
          <span
            className={cn(
              'text-[44px] font-semibold leading-none tracking-[-0.025em] tabular-nums',
              needsAttention > 0 ? 'text-foreground' : 'text-success',
            )}
          >
            {fmt.number(needsAttention)}
          </span>
          {needsAttention === 0 && (
            <span className="text-[13px] font-medium text-success">All clear</span>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[12.5px]">
        <StatusChip
          color={k.paymentPendingOrders > 0 ? 'warning' : 'muted'}
          label="Payment pending"
          value={k.paymentPendingOrders}
        />
        <StatusChip
          color={k.returnsPending > 0 ? 'warning' : 'muted'}
          label="Returns pending"
          value={k.returnsPending}
        />
        <StatusChip
          color={k.refundsPending > 0 ? 'warning' : 'muted'}
          label="Refunds pending"
          value={k.refundsPending}
        />
        <StatusChip
          color={k.alertsCount > 0 ? 'destructive' : 'muted'}
          label="Alerts"
          value={k.alertsCount}
        />
      </div>

      <div className="hidden text-muted-foreground sm:block">
        <Sparkline data={trend} width={120} height={36} area />
      </div>
    </div>
  );
}

function StatusChip({
  color,
  label,
  value,
}: {
  color: 'success' | 'info' | 'warning' | 'destructive' | 'muted';
  label: string;
  value: number;
}) {
  const dot = {
    success: 'bg-success',
    info: 'bg-info',
    warning: 'bg-warning',
    destructive: 'bg-destructive',
    muted: 'bg-muted-foreground/40',
  }[color];
  return (
    <span className="inline-flex items-baseline gap-1.5">
      <span className={cn('h-1.5 w-1.5 self-center rounded-full', dot)} />
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono font-medium tabular-nums text-foreground">{value}</span>
    </span>
  );
}

// ── KPI strip ──────────────────────────────────────────────────────────

type Tone = 'default' | 'info' | 'warning' | 'success' | 'destructive' | 'primary' | 'accent';

const TONE_ICON_BG: Record<Tone, string> = {
  default: 'bg-muted text-muted-foreground',
  primary: 'bg-muted text-foreground',
  accent: 'bg-accent-50 text-accent-700',
  info: 'bg-info/10 text-info',
  warning: 'bg-warning/15 text-[hsl(38_85%_30%)]',
  success: 'bg-success/10 text-success',
  destructive: 'bg-destructive/10 text-destructive',
};

function KpiStrip({
  loading,
  overview,
}: {
  loading: boolean;
  overview: DashboardOverview | undefined;
}) {
  if (loading || !overview) {
    return (
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-5">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="rounded-lg border border-border bg-card p-4">
            <Skeleton className="h-7 w-7 rounded-md" />
            <Skeleton className="mt-3 h-3 w-20" />
            <Skeleton className="mt-2 h-7 w-14" />
            <Skeleton className="mt-2 h-3 w-32" />
          </div>
        ))}
      </div>
    );
  }
  const k = overview.kpis;
  const ordersTrend = overview.throughput.map((p) => p.orders);
  const dispatchTrend = overview.throughput.map((p) => p.dispatches);
  const returnsTrend = overview.throughput.map((p) => p.returns);
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-5">
      <KpiTile
        icon={Boxes}
        label="Total stock"
        value={fmt.compact(k.totalStock)}
        unit="units"
        hint="across warehouses"
      />
      <KpiTile
        icon={Package}
        tone="primary"
        label="Available FG"
        value={fmt.compact(k.availableFinishedGoods)}
        unit="units"
        hint="ready to allocate"
      />
      <KpiTile
        icon={ShoppingCart}
        label="Pending orders"
        value={fmt.number(k.pendingOrders)}
        trend={ordersTrend}
        hint="awaiting confirmation"
      />
      <KpiTile
        icon={CreditCard}
        tone={k.paymentPendingOrders > 0 ? 'warning' : 'default'}
        label="Payment pending"
        value={fmt.number(k.paymentPendingOrders)}
        hint={k.paymentPendingValue ? fmt.inr(k.paymentPendingValue) : undefined}
      />
      <KpiTile
        icon={Truck}
        tone="accent"
        label="Ready for dispatch"
        value={fmt.number(k.readyForDispatch)}
        trend={dispatchTrend}
        hint="picked & packed"
      />
      <KpiTile
        icon={PackageCheck}
        tone="success"
        label="Delivered (30d)"
        value={fmt.number(k.delivered)}
        trend={dispatchTrend}
        hint="last 30 days"
      />
      <KpiTile
        icon={Undo2}
        tone={k.returnsPending > 0 ? 'warning' : 'default'}
        label="Returns pending"
        value={fmt.number(k.returnsPending)}
        trend={returnsTrend}
        hint="awaiting QC"
      />
      <KpiTile
        icon={IndianRupee}
        tone={k.refundsPending > 0 ? 'warning' : 'default'}
        label="Refunds pending"
        value={fmt.number(k.refundsPending)}
        hint={k.refundsPendingValue ? fmt.inr(k.refundsPendingValue) : undefined}
      />
    </div>
  );
}

function KpiTile({
  icon: Icon,
  label,
  value,
  unit,
  hint,
  trend,
  tone = 'default',
}: {
  icon: typeof Boxes;
  label: string;
  value: React.ReactNode;
  unit?: string;
  hint?: string;
  trend?: number[];
  tone?: Tone;
}) {
  return (
    <div className="group relative rounded-lg border border-border bg-card p-4 transition-colors hover:border-border/100 hover:bg-muted/30">
      <div className="flex items-start justify-between gap-3">
        <span className={cn('grid h-7 w-7 place-items-center rounded-md', TONE_ICON_BG[tone])}>
          <Icon className="h-[14px] w-[14px]" strokeWidth={2} />
        </span>
        {trend && trend.length > 1 && (
          <span className="text-muted-foreground/70">
            <Sparkline data={trend} width={64} height={20} />
          </span>
        )}
      </div>
      <div className="mt-3 text-[11.5px] font-medium text-muted-foreground">{label}</div>
      <div className="mt-0.5 flex items-baseline gap-1">
        <span className="text-[24px] font-semibold leading-none tracking-[-0.02em] text-foreground tabular-nums">
          {value}
        </span>
        {unit && <span className="text-[11px] font-medium text-muted-foreground">{unit}</span>}
      </div>
      {hint && <div className="mt-1.5 text-[11.5px] text-muted-foreground">{hint}</div>}
    </div>
  );
}

// ── Attention feed ─────────────────────────────────────────────────────

function AttentionFeed({
  loading,
  alerts,
  kpis,
}: {
  loading: boolean;
  alerts: OperationalAlert[];
  kpis: DashboardOverview['kpis'] | undefined;
}) {
  if (loading) {
    return <div className="px-5 py-6 text-sm text-muted-foreground">Loading alerts…</div>;
  }

  // Synthesize implicit attention items from KPIs when no explicit alerts.
  const synthesized: { id: string; tone: Tone; label: string; meta: string; to: string }[] = [];
  if (kpis) {
    if (kpis.paymentPendingOrders > 0) {
      synthesized.push({
        id: 'payments',
        tone: 'warning',
        label: 'Payment-pending orders',
        meta: `${kpis.paymentPendingOrders} orders${kpis.paymentPendingValue ? ` · ${fmt.inr(kpis.paymentPendingValue)}` : ''}`,
        to: '/orders',
      });
    }
    if (kpis.returnsPending > 0) {
      synthesized.push({
        id: 'returns',
        tone: 'warning',
        label: 'Returns awaiting QC',
        meta: `${kpis.returnsPending} requests`,
        to: '/returns',
      });
    }
    if (kpis.refundsPending > 0) {
      synthesized.push({
        id: 'refunds',
        tone: 'warning',
        label: 'Refunds in queue',
        meta: kpis.refundsPendingValue ? fmt.inr(kpis.refundsPendingValue) : `${kpis.refundsPending} refunds`,
        to: '/refunds',
      });
    }
  }

  // Use real alerts first, then synthesized; cap at 6.
  if (alerts.length === 0 && synthesized.length === 0) {
    return (
      <div className="px-5 py-10 text-center">
        <Activity className="mx-auto h-5 w-5 text-success" />
        <div className="mt-2 text-[12.5px] text-muted-foreground">No alerts. All systems nominal.</div>
      </div>
    );
  }

  if (alerts.length > 0) {
    return <AlertsList alerts={alerts} />;
  }

  return (
    <ul className="divide-y divide-border/60">
      {synthesized.slice(0, 6).map((it) => (
        <li key={it.id}>
          <Link
            to={it.to}
            className="flex items-center justify-between gap-3 px-5 py-3 transition-colors hover:bg-muted/30"
          >
            <div className="flex min-w-0 items-start gap-2.5">
              <span className={cn('mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full', TONE_ICON_BG[it.tone])}>
                <AlertTriangle className="h-3 w-3" strokeWidth={2} />
              </span>
              <div className="min-w-0 space-y-0.5">
                <div className="truncate text-[13px] font-medium text-foreground">{it.label}</div>
                <div className="text-[11.5px] text-muted-foreground">{it.meta}</div>
              </div>
            </div>
            <ArrowUpRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60" />
          </Link>
        </li>
      ))}
    </ul>
  );
}
